/**
 * Nyx — landing page composition root.
 *
 * Ownership:
 *   - useCurrentAlbum: URL ⇄ React state for the active album
 *   - usePageRebind:   1100ms cascade orchestrator on album swap (Spec §6.2)
 *
 * On swap the picker calls `rebind(id)` (not `setCurrent` directly). The
 * orchestrator handles the flash → state-swap → cascade pipeline; child
 * sections (Hero, Manifesto, DeepDive, Songs, Footer, Topbar) remount via
 * `key={themeKey}` so their entrance animations replay.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Topbar } from './components/Topbar';
import { Hero } from './components/Hero';
import { Manifesto } from './components/Manifesto';
import { DeepDive } from './components/DeepDive/DeepDive';
import { Songs } from './components/Songs/Songs';
import { Waitlist } from './components/Waitlist';
import { Footer } from './components/Footer';
import { Picker } from './components/Picker/Picker';
import { PickerTrigger } from './components/Picker/PickerTrigger';
import { SeoHead } from './components/SeoHead';
import { useCurrentAlbum } from './state/useCurrentAlbum';
import { usePageRebind } from './animation/usePageRebind';
import { NarrowScreenBanner } from './components/NarrowScreenBanner';
import { AnalyticsProvider } from './analytics';
import { LAUNCH_PHASE } from './copy/hero';

export default function App(): JSX.Element {
  const { id, album, setCurrent } = useCurrentAlbum();
  const { themeKey, isSwapping, rebind } = usePageRebind({ currentId: id, setCurrent });

  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const pickAlbum = useCallback((nextId: string) => rebind(nextId), [rebind]);

  // Reflect the current album on <html> so palette CSS vars cascade everywhere.
  // Prerender already writes these inline for the initial album; this useEffect
  // keeps them in sync on client-side album swaps.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-album', album.library.id);
    const palette = album.library.palette;
    root.style.setProperty('--nyx-album-bg', palette.bg);
    root.style.setProperty('--nyx-album-fg', palette.fg);
    root.style.setProperty('--nyx-album-accent', palette.accent);
  }, [album.library.id, album.library.palette]);

  return (
    <AnalyticsProvider album={id} phase={LAUNCH_PHASE}>
      <SeoHead album={album} />
      <NarrowScreenBanner />
      <div className="nl-rebind-flash" aria-hidden="true" />

      <Topbar key={`topbar-${themeKey}`}>
        <PickerTrigger
          ref={triggerRef}
          album={album}
          onClick={openPicker}
          aria-expanded={pickerOpen}
        />
      </Topbar>

      <main className={isSwapping ? 'is-swapping' : undefined}>
        <Hero key={`hero-${themeKey}`} album={album} />
        <Manifesto key={`manifesto-${themeKey}`} album={album} />
        <DeepDive key={`dd-${themeKey}`} album={album} />
        <Songs key={`songs-${themeKey}`} album={album} />
        <Waitlist />
        <Footer key={`footer-${themeKey}`} album={album} />
      </main>

      <Picker
        open={pickerOpen}
        currentId={id}
        onClose={closePicker}
        onPick={pickAlbum}
        triggerRef={triggerRef}
      />
    </AnalyticsProvider>
  );
}
